import React, { useState, useEffect, useMemo } from 'react';
import HeaderWithIcon from '../../components/common/HeaderWithIcon';
import GameImage1 from './../../assets/images/games-1.webp?react';
import CommissionCard from './CommissionCard';
import CommissionBar from './CommissionBar';

import { commissionsApi } from '../../api/commissions';
import { userApi } from '../../api/user';
import { gamesApi } from '../../api/games';
import { useAuth } from '../../context/AuthContext';
import getGameType from '../../utils/games';
import { getGameImage } from '../../utils/getGameImage';
import LoadingPng from '../../assets/images/Loading.png';

import { Pagination } from 'flowbite-react';
import dayjs from 'dayjs';
import { toast } from 'react-hot-toast';

const commissionTypes = [];

export default function Commission() {
  const { user, tokens } = useAuth();
  const [selectedType, setSelectedType] = useState('all');
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [gameSettings, setGameSettings] = useState([]);
  const limit = 10;

  // Add state for game totals
  const [gameTotals, setGameTotals] = useState({ gameSettingsTotals: {}, gameTotals: {} });
  // Track if we're fetching totals
  const [loadingTotals, setLoadingTotals] = useState(false);

  const [userData, setUserData] = useState({
    meta: { commissions: '0.00' },
    commissions: '0.00',
  });

  useEffect(() => {
    const fetchGameSettings = async () => {
      const response = await gamesApi.getAllGames(tokens.access.token);
      setGameSettings(response.data);
    };
    fetchGameSettings();
  }, [tokens.access.token]);

  const fetchUser = async () => {
    try {
      const response = await userApi.getUserById(tokens.access.token, user.id);
      if (response.status === 200) {
        // Ensure we're getting the user object from the response
        const userData = response.data.user || response.data;
        setUserData(userData);
        // Update localStorage
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        localStorage.setItem(
          'user',
          JSON.stringify({
            ...storedUser,
            ...userData,
          })
        );
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  useEffect(() => {
    fetchUser();
  }, [tokens.access.token]);

  // Fetch all commissions data to calculate game totals
  const fetchAndCalculateTotals = async () => {
    setLoadingTotals(true);
    try {
      // Fetch all commissions with a large limit
      const response = await commissionsApi.getAllCommissions(tokens.access.token);
      
      if (response.status === 200) {
        // Calculate totals using the utility function
        const totals = commissionsApi.calculateGameTotals(response.data.rows);
        setGameTotals(totals);
      }
    } catch (err) {
      console.error('Error calculating commission totals:', err);
    } finally {
      setLoadingTotals(false);
    }
  };

  const fetchCommissions = async (page = currentPage, filterType = selectedType) => {
    setLoading(true);
    try {
      const response = await commissionsApi.getCommissions(
        tokens.access.token,
        page,
        limit
      );
      if (response.status === 200) {
        setCommissions(response.data.rows);
        setTotalPages(Math.ceil(response.data.count / limit));
        
        // If we haven't calculated totals yet, do it now
        if (Object.keys(gameTotals.gameSettingsTotals).length === 0) {
          fetchAndCalculateTotals();
        }
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCommissions(currentPage, selectedType);
  }, [tokens.access.token, currentPage]);

  const getImage = (gameSettingsId) => {
    const gameSetting = gameSettings.find((game) => game.id === gameSettingsId);
    if (!gameSetting?.otherSettings?.image) return GameImage1; // Default fallback

    const gameType = getGameType(gameSetting.otherSettings.image);
    const gameImage = getGameImage(gameType);
    return gameImage;
  };

  const getTotalAmount = (apiData) => {
    let totalAmount = 0;
    if(apiData.game && apiData.game.transactionBets){
      for(let i = 0; i < apiData.game.transactionBets.length; i++){
        totalAmount += parseFloat(apiData.game.transactionBets[i].amount);
      }
      return totalAmount;
    }else{
      return apiData.TransactionReceipt.totalAmount;
    }
  }

  const formatCommissionData = (apiData) => ({
    id: apiData.id,
    game: apiData.game?.name || apiData.TransactionReceipt?.game?.name,
    logo: getImage(apiData.TransactionReceipt?.game?.gameSettingsId),
    drawTime: apiData.TransactionReceipt?.drawTime
      ? dayjs(`2000-01-01 ${apiData.TransactionReceipt.drawTime}`).format(
          'h:mm A'
        )
      : 'N/A',
    drawDate: dayjs(apiData.created_at).format('MMM DD, YYYY hh:mmA'),
    source: apiData.source_user?.name || 'N/A',
    owner: apiData.User?.name || 'N/A',
    amount: parseFloat(apiData.TransactionReceipt?.totalAmount || 0).toFixed(2),
    commission: parseFloat(apiData.amount || 0).toFixed(2),
    user: apiData.User?.name || 'Unknown User',
    ticketId: apiData.transactionReceiptsId,
    transactionReciept: apiData.TransactionReceipt,
    totalAmountz: getTotalAmount(apiData),
    gameData: apiData.game,
  });

  // Group commissions by transactionReceiptsId
  const groupCommissionsByTicketId = (commissions) => {
    const ticketGroups = {};

    commissions.forEach((commission) => {
      if (!ticketGroups[commission.ticketId]) {
        ticketGroups[commission.ticketId] = {
          items: [commission],
          count: 1,
        };
      } else {
        ticketGroups[commission.ticketId].items.push(commission);
        ticketGroups[commission.ticketId].count++;
      }
    });

    return ticketGroups;
  };

  // Update filtering to handle TransactionReceipt structure
  const filteredCommissions = commissions
    .filter(
      (commission) => {
        if (selectedType === 'all') {
          return true;
        }
        
        // Check if filtering by specific game ID
        if (selectedType.startsWith('game:')) {
          const gameId = selectedType.split(':')[1];
          return commission.gameId?.toString() === gameId || 
                 commission.TransactionReceipt?.gameId?.toString() === gameId;
        }
        
        // Use game_settings_id for filtering
        const gameSettingsId = commission.game?.game_settings_id?.toString() || 
                              commission.TransactionReceipt?.game?.game_settings?.id?.toString();
        return gameSettingsId === selectedType;
      }
    )
    .map(formatCommissionData);

  // Group the commissions
  const groupedCommissions = groupCommissionsByTicketId(filteredCommissions);

  const handleWalletUpdate = async (updatedWallet) => {
    try {
      // Fetch updated user data
      const response = await userApi.getUserById(tokens.access.token, user.id);
      if (response.status === 200) {
        // Get user data from response
        const userData = response.data.user || response.data;
        setUserData(userData);

        // Update localStorage with all user data
        localStorage.setItem('user', JSON.stringify(userData));

        // Refresh user data to ensure all components have the latest data
        fetchUser();
      }

      // Refresh commissions data
      fetchCommissions();
      // Refresh totals
      fetchAndCalculateTotals();
    } catch (error) {
      toast.error('Failed to update user data');
      console.error(error);
    }
  };

  const handleFilterChange = (filterType) => {
    setSelectedType(filterType);
    // Reset to first page when filter changes
    setCurrentPage(1);
    // Fetch commissions with the new filter
    fetchCommissions(1, filterType);
  };

  return (
    <div className='min-h-screen relative overflow-hidden font-poppins'>
      {loading && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-white bg-opacity-75'>
          <img
            src={LoadingPng}
            alt='Loading'
            className='w-16 h-16 animate-spin'
          />
        </div>
      )}
      <HeaderWithIcon title='Commissions' />
      <div className='px-5 pt-4'>
        <CommissionBar
          onFilterChange={handleFilterChange}
          commissionTypes={commissionTypes}
          selectedType={selectedType}
          userData={userData}
          onWalletUpdate={handleWalletUpdate}
          gameTotals={gameTotals}
          isLoadingTotals={loadingTotals}
        />

        <>
          <div className='divide-y divide-gray-200'>
            {Object.keys(groupedCommissions).map((ticketId, index) => (
              <div
                key={ticketId}
                className={
                  groupedCommissions[ticketId].count > 1
                    ? 'bg-yellow-100 pl-1 mb-5 rounded-md'
                    : 'mb-2'
                }
              >
                {groupedCommissions[ticketId].items.map((commission, index) => (
                  <div key={commission.id} className='relative'>
                    <CommissionCard
                      {...commission}
                      isGrouped={groupedCommissions[ticketId].count > 1}
                      isFirstInGroup={index === 0}
                      isLastInGroup={
                        index === groupedCommissions[ticketId].items.length - 1
                      }
                      isLastItem={
                        index === groupedCommissions[ticketId].items.length - 1
                      }
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>

        {totalPages > 1 && (
          <div className='flex justify-center mt-4 mb-5'>
            <Pagination
              currentPage={currentPage}
              layout='navigation'
              onPageChange={(page) => setCurrentPage(page)}
              showIcons
              totalPages={totalPages}
            />
          </div>
        )}
      </div>
    </div>
  );
}
